import { stubFalse, uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { SubmittedOrder } from '../../../app/domain/Order/SubmittedOrder';
import { Product, getValidThumbUrl } from '../../../app/domain/Product/Product';
import { getSubmittedOrder } from '../../../app/initApp';
import { copySubmittedOrder } from '../../../app/usecases/copySubmittedOrder'
import { formatDate } from '../../util/formatDate';
import { isMobile } from '../../util/isMobile';
import useMedia from '../../util/useMedia';
import Button from '../misc/Button';

// Styles
import styles from './OrdersSummary.module.css';

interface Props {
  className: string;
  onOrderClick: Function;
  orders: SubmittedOrder[];
  partnerData?: any;
}

let initPageSize = 10;
const loading = [];

const maybeLoadOrder = (item: SubmittedOrder) => {
  const ordersKey = item.order.ordersKey;

  if (!item.orderStatus && loading.indexOf(ordersKey) === -1) {
    loading.push(ordersKey);
    getSubmittedOrder({ ordersKey, partnersKey: item.orderStateSnippet['partnerInfo'].partnersKey });
  }
}

const OrdersSummary = (props: Props) => {
  const [currentCount, setCurrentCount] = useState(initPageSize);
  const orders = props.orders.slice(0, currentCount);
  const disableMoreButton = orders.length === props.orders.length;
  const [selectedOrder, setSelectedOrder] = useState<string>("");
  const [isOrderSelected, setIsOrderSelected] = useState<boolean>(false);
  let container = props.className || '';
  let matchMedia = useMedia("(max-width: 500px)");
  const mobile = (isMobile() || matchMedia) ? true : false;
  const [currentTop, setCurrentTop] = useState<any>(null);
  const img: any = useRef([]);

  function loadMore() {
    const size = currentCount + 2;
    // Cache so we have keep the size between modal showings
    initPageSize = size;
    setCurrentCount(size);
  }

  function handleClick(e, submittedOrder, key) {
    const target = e.currentTarget;
    const ordersScrollTarget =  target.parentNode.parentElement.scrollTop;
    if (target) {
      let viewportOffset = target.getBoundingClientRect();
      let top = mobile ? viewportOffset.top : (ordersScrollTarget > 0 ? (viewportOffset.top - 70) : 'unset');
      setCurrentTop(top);
    }
    setIsOrderSelected(true);
    setSelectedOrder(key);
    props.onOrderClick(submittedOrder.order.ordersKey);
  }

  useEffect(() => {
    orders.forEach((item) => {
      maybeLoadOrder(item);
    });
  }, [currentCount]);

  container += ' ' + (isOrderSelected ? styles.noClick : '') + ' ' + styles.footerMargin;

  return <div className={[container, styles.submittedSummary].join(' ')}>
    {
      props.orders.length > 0
        ?
        <React.Fragment>
          {orders.map((submittedOrder: SubmittedOrder, index: number) => {
            const date = formatDate(submittedOrder.createdOn);
            const columnData = getColumnData(submittedOrder);
            const products = submittedOrder?.orderState;
            const partner = props.partnerData;
            const key = submittedOrder.id;
            const status =
              columnData.orderStatus.toLowerCase() === "cancelled"
                ? styles.redColor : (columnData.orderStatus.toLowerCase() === "complete" ? styles.greenColor : styles.blueColor);

            const Copy = () => {
              return <Button
                disabled={key === selectedOrder}
                className={styles.loadMoreButton + ' partner-background-color1'}
                iconCls={key === selectedOrder && "fas fa-circle-notch fa-spin"}
                label={(key === selectedOrder && !mobile) ? "" : "COPY"}
                onClick={(e) => {
                  setSelectedOrder(key);
                  copySubmittedOrder({ ordersKey: submittedOrder.order.ordersKey, partnersKey: partner.partnersKey });
                }} />
            }

            const Columns = () => {
              return <>
                <div className={styles.column}>
                  <span className={styles.mobileHeader}>ITEMS</span>
                  {columnData.productCount}
                </div>
                <div className={styles.column}>
                  <span className={styles.mobileHeader}>Order #</span>
                  $ {columnData.key}
                </div>
                <div className={styles.column}>
                  <span className={styles.mobileHeader}>DATE</span>
                  {date}
                </div>
                <div className={styles.column}>
                  <span className={styles.mobileHeader}>STATUS</span>
                  {columnData.orderStatus}
                </div>
                {!mobile ? <div className={styles.column}>
                  <span className={styles.mobileHeader}>COPY</span>
                  <div className={styles.copyBtn}>
                    <Copy />
                  </div>
                </div> : null}
              </>
            }

            const Loader = () => {
              return <>
                {key === selectedOrder
                  ?
                  <div
                    className={styles.selectedOrder} style={{ top: currentTop }}>
                    <i className='icon fas fa-circle-notch fa-spin'></i>
                    <span>Loading Order</span>
                  </div>
                  : null
                }
              </>
            }

            return <>
              {mobile
                ?
                <div className={styles.orderItem} key={key} onClick={(e) => handleClick(e, submittedOrder, key)}>
                  <div className={styles.thumbContainer}>
                    {products && products.map(function (product: any, index: number) {
                      return (index <= 2) && <img
                        key={uniqueId()}
                        className={styles.imageColumn}
                        src={getValidThumbUrl({
                          Size: 100,
                          Camera: 'Front',
                          DocId: product.SavedDocId,
                          cadworxSessionId: partner.cadworxSessionId,
                          type: "img"
                        }, submittedOrder.orderState[index])} alt="front" />
                    })}
                  </div>
                  <div className={styles.itemContainer}>
                    <Columns />
                    <div className={styles.column}>
                      <span className={styles.mobileHeader}>COPY</span>
                      <div className={styles.copyBtn}>
                        <div
                          className={styles.copyLink}
                          onClick={(e) => {
                            copySubmittedOrder({ ordersKey: submittedOrder.order.ordersKey, partnersKey: partner.partnersKey });
                          }}
                        ><span
                          className="fas fa-copy partner-color1"></span> COPY</div>
                      </div>
                    </div>
                  </div>
                  <Loader />
                </div>
                :
                <div className={styles.listGroup} key={key}>
                  <div className={styles.listGroupHeader}>
                    <span className={styles.date}>{date}</span>
                  </div>
                  <div className={styles.itemInfo}>
                    <div className={styles.data}>
                      <div className={[(products?.length >= 2 ? styles.thumbContainer : styles.images)].join(' ')}>
                        {products && products.map(function (product: any, index: number) {
                          return (index <= 2) && <img
                            ref={el => img.current[0] = el}
                            key={uniqueId()}
                            className={styles.imageColumn}
                            src={getValidThumbUrl({
                              Size: 100,
                              Camera: 'Front',
                              DocId: product.SavedDocId,
                              cadworxSessionId: partner.cadworxSessionId,
                              type: "img"
                            }, submittedOrder.orderState[index])} alt="front" />
                        })}
                      </div>
                    </div>
                    <div className={styles.data}>
                      <div className={styles.description}>
                        <span>Status</span>
                        <span className={[status, styles.status].join(' ')}>{columnData.orderStatus?.toUpperCase()}</span>
                      </div>
                    </div>
                    <div className={styles.data}>
                      <div className={styles.description}>
                        <span>Items: {columnData.productCount}</span>
                        <span>Order #: <b className={styles.total}>{columnData.key}</b></span>
                      </div>
                    </div>
                    <div className={[styles.data, styles.btn].join(' ')}>
                      <div className={styles.copyBtn}>
                        <Copy />
                      </div>
                    </div>
                  </div>
                  <Loader />
                </div>
              }
              {index !== (orders.length - 1) ? <div className={styles.orderDivider}></div> : null}</>
          })}

          <div className={styles.divider}></div>
          <div className={styles.loadMore}>
            <Button
              className={styles.loadMoreButton + ' partner-background-color1'}
              disabled={disableMoreButton}
              label="LOAD MORE..."
              onClick={() => {
                loadMore();
              }} />
          </div>
        </React.Fragment>
        :
        <div className={styles.noItemsFound}>No items found...</div>
    }
  </div>
}

function getColumnData(order: SubmittedOrder) {
  const orderState = order.orderState || [];
  const orderStatus = order.orderStatus;
  let productCount = 0;
  let total = 0;

  orderState.forEach((productData: Product) => {
    productCount += productData.TotalQuantity;
    total += productData.TotalPrice;
  });

  total = parseFloat(total.toFixed(2));

  return {
    orderStatus: orderStatus ? orderStatus.displayNames.general : 'Loading...',
    productCount,
    total,
    key: order.order.ordersKey
  };
}

export default OrdersSummary;